import type { InputCluster, PlacedBox } from "./graphLayout";
import { SKIP_CHANNELS } from "./skipChannels";
import type { GraphNode } from "./workflowGraph";

/**
 * The vertical layout of one level (F59): for phones, narrow windows and the split view. The
 * pipeline is one column on the left, top to bottom, its cards aligned on the left edge; the
 * edges between its steps run in channels beside it (`edgeRoutes`). Each cluster of inputs
 * is a column of its own, in the order its lines run, to the right of the pipeline, its
 * bottom just above the first step it feeds; so every input's line leaves it with nothing
 * beside it, and drops into its step's tab from the gap above. Clusters stack
 * downwards; when one is taller than the room above its step, the step moves down. Inputs
 * that lead to no step come last, under the others. Pure: it only places the boxes.
 */

export const COLUMN_LAYOUT = {
  /** Between a cluster's bottom and the top of the step it feeds: room for the line to curve into the tab. */
  feedRise: 48,
  /** Between two steps, at least: a feeding line curves in the gap above a step. */
  stepGap: 56,
  /** Between the channels right of the column and the inputs. */
  sideGap: 32,
  /** Between two clusters. */
  clusterGap: 24,
} as const;

/** How many channels a column needs on each side: one for the edges to the next step, one per lane of a skipping edge. */
function columnChannels(lanes: number): number {
  return 1 + lanes;
}

/** How far channels reach from the column's side: their margin and spacing (`SKIP_CHANNELS`). */
export function channelsDepth(lanes: number): number {
  return SKIP_CHANNELS.runMargin + columnChannels(lanes) * SKIP_CHANNELS.runSpacing;
}

interface ColumnOptions {
  /** The gap after a pipeline card, before the next: room for the edges that cross it. */
  readonly gapAfter: (above: string, below: string) => number;
  /** How many edges skip steps at this level. */
  readonly lanes: number;
}

/** Places the level's boxes; returns where the channels right of the column end. */
export function placeColumn(main: readonly GraphNode[], clusters: readonly InputCluster[], placed: ReadonlyMap<string, PlacedBox>, options: ColumnOptions): { readonly channelsRight: number } {
  const boxOf = (id: string): PlacedBox => {
    const box = placed.get(id);
    if (box === undefined) throw new Error(`column layout: no node ${id}`);
    return box;
  };
  const columnLeft = channelsDepth(options.lanes);
  const columnRight = columnLeft + Math.max(0, ...main.map((node) => boxOf(node.id).width));
  const channelsRight = columnRight + channelsDepth(options.lanes);
  const clusterLeft = channelsRight + COLUMN_LAYOUT.sideGap;
  const stacked = clusters.map((cluster) => stack(cluster, boxOf));
  const byAnchor = new Map(stacked.flatMap((cluster) => (cluster.anchor === null ? [] : [[cluster.anchor, cluster] as const])));
  const place = (cluster: InputCluster, top: number) => {
    for (const id of cluster.members) {
      const box = boxOf(id);
      box.x += clusterLeft;
      box.y += top;
    }
  };

  let cursor = 0;
  // The lowest cluster's bottom so far: the next one starts under it.
  let clustersBottom = Number.NEGATIVE_INFINITY;
  main.forEach((node, index) => {
    const box = boxOf(node.id);
    const cluster = byAnchor.get(node.id);
    let top = cursor;
    if (cluster !== undefined) {
      top = Math.max(cursor, clustersBottom + COLUMN_LAYOUT.clusterGap + cluster.size.height + COLUMN_LAYOUT.feedRise);
      clustersBottom = top - COLUMN_LAYOUT.feedRise;
      place(cluster, clustersBottom - cluster.size.height);
    }
    box.x = columnLeft;
    box.y = top;
    const next = main[index + 1];
    cursor = top + box.height + (next === undefined ? 0 : Math.max(COLUMN_LAYOUT.stepGap, options.gapAfter(node.id, next.id)));
  });
  for (const cluster of stacked.filter((candidate) => candidate.anchor === null || !main.some((node) => node.id === candidate.anchor))) {
    const top = Number.isFinite(clustersBottom) ? clustersBottom + COLUMN_LAYOUT.clusterGap : 0;
    place(cluster, top);
    clustersBottom = top + cluster.size.height;
  }
  return { channelsRight };
}

/**
 * A cluster as one column: its members top to bottom in the order of the cluster's own layout
 * (its ranks run downwards), each its line's drop below the one above.
 */
function stack(cluster: InputCluster, boxOf: (id: string) => PlacedBox): InputCluster {
  const boxes = cluster.members.map((id) => boxOf(id)).sort((upper, lower) => upper.y - lower.y || upper.x - lower.x);
  let top = 0;
  for (const box of boxes) {
    box.x = 0;
    box.y = top;
    top += box.height + COLUMN_LAYOUT.feedRise;
  }
  return { ...cluster, size: { width: Math.max(0, ...boxes.map((box) => box.width)), height: Math.max(0, top - COLUMN_LAYOUT.feedRise) } };
}
